import * as Sentry from '@sentry/browser';
import moment from 'moment';
import {
  REMOTE_TEXT_COMMANDS,
  REMOTE_BLACKBOARD_COMMANDS,
  TLVX_COMMANDS,
  TLVX_LIGHT_COMMANDS,
  TLVX_PHOTO_COMMANDS,
  ADJUST_SCREEN_COMMANDS,
} from '../lib/constants';
import {
  stickerStart,
  stickerHide,
  paintStart,
  paintStop,
  paintAllClear,
  remoteControlStart,
  remoteControlEnd,
  toggleSound,
  sendDataCurrentTarget,
  sendDataJoinedMembers,
} from '../lib/remote';
import messages from '../lib/i18n';
import config from '../config';
import {
  saveLocalStorage,
  loadLocalStorage,
  validationRemoteSendTextInfo,
} from '../lib/tools';
import * as actionTypes from '../actions/actionTypes';

const lsKey = config.localStorageKey.remote;
const { locale } = config;
const i18nMessages = messages[locale];

const sendDataToDevice = sendTextInfo => {
  try {
    const { title, titleSuffix, subtitle, note } = sendTextInfo;
    const date = moment().format('YYYY-MM-DD');
    const suffixValue =
      i18nMessages['remotePage.bs.label.letters.suffixes'][titleSuffix.value];
    const inputText = {
      name: `${title.value}${(title.value && suffixValue) || ''}`,
      type: subtitle.value,
      date,
      text: note.value,
    };
    // 文字列送信と同時にクライアント画面に表示する
    sendDataCurrentTarget(
      REMOTE_TEXT_COMMANDS.sendString,
      JSON.stringify(inputText),
    );
    return true;
  } catch (error) {
    Sentry.captureException(
      new Error(`[ERROR]SEND_REMOTE_BLACKBOARD_TEXT:${error}`),
    );
    return false;
  }
};

/**
 * 黒板UI情報に関する送信処理
 * @param {*} blackboardInfo
 */
const sendBlackboardData = blackboardInfo => {
  const { colors, positions } = config.blackboardParam;
  if (blackboardInfo.isShow) {
    sendDataCurrentTarget(REMOTE_BLACKBOARD_COMMANDS.show);
  } else {
    sendDataCurrentTarget(REMOTE_BLACKBOARD_COMMANDS.hide);
  }
  sendDataCurrentTarget(
    REMOTE_BLACKBOARD_COMMANDS.color,
    colors[blackboardInfo.color],
  );
  sendDataCurrentTarget(
    REMOTE_BLACKBOARD_COMMANDS.opacity,
    blackboardInfo.opacity,
  );
  sendDataCurrentTarget(
    REMOTE_BLACKBOARD_COMMANDS.position,
    positions[blackboardInfo.position],
  );
};

export const appInitialState = {
  myOperatorId: 0,
  isWaiting: true,
  isSticker: false,
  isPaint: false,
  isSound: true, // デフォルトマイクオン
  isRemoteControl: false,
  isVideo: false, // 転送方式（false:画面キャプチャ方式(100)/true:カメラ映像転送方式(200)
  isLight: false,
  isPause: false,
  sendTextInfo: {
    title: { value: '', error: false },
    titleSuffix: { value: '', error: false },
    subtitle: { value: '', error: false },
    note: { value: '', error: false, errorType: undefined },
  },
  isBlackboard: false,
  blackboardInfo: {
    isShow: false,
    color: 'green',
    opacity: '50',
    position: 'right_top',
  },
  colorIndex: 0,
  callHistories: [],
};

export default (state = appInitialState, action) => {
  switch (action.type) {
    case 'SUCCESS_API_GET_USER_INFO': {
      const userInfo = action.payload;
      const { operatorId: myOperatorId } = userInfo;
      return { ...state, myOperatorId };
    }
    case 'ENTER_ROOM': {
      const isWaiting = false;
      const blackboardInfo = {
        isShow: state.blackboardInfo.isShow,
        color: loadLocalStorage(lsKey.color) || state.blackboardInfo.color,
        opacity:
          loadLocalStorage(lsKey.opacity) || state.blackboardInfo.opacity,
        position:
          loadLocalStorage(lsKey.position) || state.blackboardInfo.position,
      };
      return { ...state, isWaiting, blackboardInfo };
    }
    /** 入室済みのルーム情報取得後処理 */
    case 'SUCCESS_API_GET_ROOM_INFO': {
      const { roomMembers } = action.payload;
      const me =
        (roomMembers &&
          roomMembers.find(
            ({ operatorId }) => operatorId === state.myOperatorId,
          )) ||
        {};
      return { ...state, colorIndex: me.index || 0 };
    }
    case 'LEAVE_ROOM': {
      const isWaiting = true;
      return { ...state, isWaiting };
    }
    case 'TOGGLE_STICKER': {
      const isSticker = !state.isSticker;
      const isPaint = false;
      const isRemoteControl = false;
      remoteControlEnd();
      if (isSticker) {
        paintAllClear();
        stickerStart();
      } else {
        stickerHide();
      }
      return { ...state, isSticker, isPaint, isRemoteControl };
    }
    case 'TOGGLE_PAINT': {
      const isPaint = !state.isPaint;
      const isSticker = false;
      const isRemoteControl = false;
      // stickerHide();
      // paintAllClear();
      remoteControlEnd();
      if (isPaint) {
        stickerHide();
        paintStart(state.colorIndex);
      } else {
        paintStop();
        paintAllClear();
      }
      return { ...state, isPaint, isSticker, isRemoteControl };
    }
    case 'TOGGLE_SOUND': {
      const isSound = !state.isSound;
      toggleSound();
      return { ...state, isSound };
    }
    case 'TOGGLE_REMOTE_CONTROL': {
      const isRemoteControl = !state.isRemoteControl;
      const isPaint = false;
      const isSticker = false;
      stickerHide();
      paintAllClear();
      if (isRemoteControl) {
        remoteControlStart();
      } else {
        remoteControlEnd();
      }
      return { ...state, isRemoteControl, isPaint, isSticker };
    }
    /** 表示モード変更 */
    case 'TOGGLE_TRANS_MODE': {
      const isVideo = !state.isVideo;

      // アプリへ通知を送る
      const data = 'changeTransMode';
      const kind =
        (isVideo && TLVX_COMMANDS.setCameraTransMode) ||
        TLVX_COMMANDS.setVideoCaptureMode;
      sendDataJoinedMembers(kind, data);

      // 遠隔操作は解除する
      remoteControlEnd();
      return { ...state, isVideo, isRemoteControl: false };
    }
    /** 表示モード変更受信 */
    case 'RECEIVED_TOGGLE_TRANS_MODE': {
      const { isVideo } = action;

      // 遠隔操作は解除する
      remoteControlEnd();
      return { ...state, isVideo, isRemoteControl: false };
    }
    case 'TOGGLE_BLACKBOARD': {
      return { ...state, isBlackboard: !state.isBlackboard };
    }
    /** ライトon/off変更 */
    case 'TOGGLE_LIGHT': {
      const isLight = !state.isLight;
      // アプリへ通知を送る
      const data = 'changeLight';
      const kind =
        (isLight && TLVX_LIGHT_COMMANDS.on) || TLVX_LIGHT_COMMANDS.off;
      sendDataJoinedMembers(kind, data);

      return { ...state, isLight: !state.isLight };
    }
    /** ライトon/off変更受信 */
    case 'RECEIVED_TOGGLE_LIGHT': {
      const { isLight } = action;
      return { ...state, isLight };
    }
    /** 一時停止on/off変更 */
    case 'TOGGLE_PAUSE': {
      const isPause = !state.isPause;
      // アプリへ通知を送る
      const data = 'changePause';
      const kind =
        (isPause && ADJUST_SCREEN_COMMANDS.pause) ||
        ADJUST_SCREEN_COMMANDS.resume;
      sendDataJoinedMembers(kind, data);

      return { ...state, isPause };
    }
    /** 一時停止on/off変更受信 */
    case 'RECEIVED_TOGGLE_PAUSE': {
      const { isPause } = action;
      return { ...state, isPause };
    }
    case 'SEND_PHOTO': {
      // アプリへ通知を送る
      const data = 'changePhoto';
      const kind = TLVX_PHOTO_COMMANDS.photo;
      sendDataJoinedMembers(kind, data);
      return state;
    }
    case 'UPDATE_REMOTE_TEXT': {
      const { params } = action;
      const getNewState = input => {
        const key = Object.keys(input)[0];
        const value = Object.values(input)[0];
        return validationRemoteSendTextInfo(key, value);
      };
      const result = getNewState(params);
      return { ...state, sendTextInfo: { ...state.sendTextInfo, ...result } };
    }
    case 'SEND_SHOW': {
      const isShow = action && action.isShow;
      const blackboardInfo = { ...state.blackboardInfo, isShow };
      sendBlackboardData(blackboardInfo);
      return { ...state, blackboardInfo };
    }
    case 'SEND_COLOR': {
      const key = action && action.key;
      const blackboardInfo = { ...state.blackboardInfo, color: key };
      saveLocalStorage(lsKey.color, key);
      sendBlackboardData(blackboardInfo);
      return { ...state, blackboardInfo };
    }
    case 'SEND_OPACITY': {
      const value = action && action.value;
      const blackboardInfo = { ...state.blackboardInfo, opacity: value };
      saveLocalStorage(lsKey.opacity, value);
      sendBlackboardData(blackboardInfo);
      return { ...state, blackboardInfo };
    }
    case 'SEND_POSITION': {
      const key = action && action.key;
      const blackboardInfo = { ...state.blackboardInfo, position: key };
      saveLocalStorage(lsKey.position, key);
      sendBlackboardData(blackboardInfo);
      return { ...state, blackboardInfo };
    }
    case 'SEND_REMOTE_BLACKBOARD_TEXT': {
      const { sendTextInfo } = state;
      const { title, titleSuffix, subtitle, note } = sendTextInfo;
      const inputError =
        title.error || titleSuffix.error || subtitle.error || note.error;
      if (!inputError) {
        const blackboardInfo = { ...state.blackboardInfo, isShow: true };
        // 入力エラー無しの場合はクライアントに送信する
        sendDataToDevice(sendTextInfo);
        sendBlackboardData(blackboardInfo);
        return { ...state, blackboardInfo };
      }
      return state;
    }
    case 'SEND_INIT': {
      const blackboardInfo = {
        isShow: false,
        color: loadLocalStorage(lsKey.color),
        opacity: loadLocalStorage(lsKey.opacity),
        position: loadLocalStorage(lsKey.position),
      };
      sendDataCurrentTarget(REMOTE_BLACKBOARD_COMMANDS.show, false);
      sendDataCurrentTarget(
        REMOTE_BLACKBOARD_COMMANDS.color,
        blackboardInfo.color,
      );
      sendDataCurrentTarget(
        REMOTE_BLACKBOARD_COMMANDS.opacity,
        blackboardInfo.opacity,
      );
      sendDataCurrentTarget(
        REMOTE_BLACKBOARD_COMMANDS.position,
        blackboardInfo.position,
      );
      return { ...state, blackboardInfo };
    }
    /** カメラズームイン */
    case 'EXPAND_REMOTE_SCREEN': {
      const data = 'expand';
      sendDataCurrentTarget(ADJUST_SCREEN_COMMANDS.expand, data);
      return state;
    }
    /** カメラズームアウト */
    case 'CONTRACT_REMOTE_SCREEN': {
      const data = 'contract';
      sendDataCurrentTarget(ADJUST_SCREEN_COMMANDS.contract, data);
      return state;
    }
    /** カメラズームリセット */
    case 'RESET_REMOTE_SCREEN': {
      const data = 'reset';
      sendDataCurrentTarget(ADJUST_SCREEN_COMMANDS.reset, data);
      return state;
    }
    case 'SUCCESS_API_GET_BLACKBOARD_PRESET_FOR_REMOTE': {
      const { blackboardPresets = [] } = action.payload;
      const isBlackboard = true;
      if (blackboardPresets.length === 0) {
        // 対象の黒板情報が取得できなかった場合は空の黒板を表示する
        return {
          ...state,
          sendTextInfo: appInitialState.sendTextInfo,
          isBlackboard,
        };
      }
      const { title, titleSuffix, subtitle, note } = blackboardPresets[0];
      let sendTextInfo = {};
      let hasError = false;
      // 取得結果を検証する
      Object.entries({
        title,
        titleSuffix,
        subtitle,
        note,
      }).forEach(([key, value]) => {
        const result = validationRemoteSendTextInfo(key, value);
        sendTextInfo = { ...sendTextInfo, ...result };
        hasError = hasError || result[key].error;
      });
      // 検証結果がOKの場合はクライアントに送信する
      if (!hasError) {
        sendDataToDevice(sendTextInfo);
      }
      return { ...state, sendTextInfo, isBlackboard };
    }
    /** アプリユーザの映像共有ボタン押下処理 */
    case 'SHARE_REMOTE_CAMERA': {
      const isPaint = false;
      const isSticker = false;
      const isRemoteControl = false;
      stickerHide();
      paintAllClear();
      remoteControlEnd();
      return { ...state, isPaint, isSticker, isRemoteControl };
    }
    case actionTypes.SUCCESS_API_GET_HISTORIES: {
      const callHistories = action.payload;
      return { ...state, callHistories };
    }
    default:
      return state;
  }
};
